import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  KeyRound,
  Laptop,
  LogOut,
  Mail,
  Monitor,
  Phone,
  Shield,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  Tablet,
  UserX,
} from 'lucide-react';
import { Avatar } from '../../components/Avatar';
import { Badge } from '../../components/Badge';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Modal } from '../../components/Modal';
import { Tabs } from '../../components/Tabs';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminUsers,
  useAuditLog,
  updateAdminUser,
  adminResetUserPassword,
  adminForceSignout,
  useUserSessions,
  revokeUserSessionAdmin,
  adminDisableUser2fa,
  type AdminUserSessionRow,
} from '../../api/admin';
import type { AdminUser } from '../../mocks/admin';
import {
  SESSION_CAP_DEFAULT,
  SESSION_CAP_MAX,
  SESSION_CAP_MIN,
} from '../../api/platform';
import { formatDate, timeAgo } from '../../lib/format';
import { cn } from '../../lib/cn';

const ROLE_LABEL: Record<AdminUser['primaryRole'], string> = {
  platform_admin: 'Platform admin',
  reseller_admin: 'Reseller admin',
  business_admin: 'Business admin',
  business_staff: 'Business staff',
};

const ROLE_TONE: Record<AdminUser['primaryRole'], 'info' | 'success' | 'warning' | 'neutral'> = {
  platform_admin: 'info',
  reseller_admin: 'success',
  business_admin: 'warning',
  business_staff: 'neutral',
};

const STATUS_TONE: Record<AdminUser['status'], 'success' | 'info' | 'neutral'> = {
  active: 'success',
  invited: 'info',
  disabled: 'neutral',
};

/**
 * UserDetail — admin view of a single user across the platform.
 * Header card + 5 tabs (Overview / Memberships / Security / Sessions / Audit).
 * The Security tab carries the per-user max-sessions override; any admin can
 * raise the cap above the platform default for VIP / shared accounts.
 */
export function UserDetail() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const { data: adminUsers = [], isLoading } = useAdminUsers();
  const user = adminUsers.find((u) => u.id === id);

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1440px] mx-auto space-y-4" aria-busy="true">
        <div className="skeleton h-8 w-64 max-w-full rounded" />
        <div className="space-y-3">
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 mb-4 transition"
        >
          <ArrowLeft className="w-4 h-4" />
          All users
        </Link>
        <Card className="p-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-zinc-100 dark:bg-zinc-800 grid place-items-center text-zinc-500 dark:text-zinc-400 mb-3">
            <UserX className="w-5 h-5" />
          </div>
          <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
            User not found
          </h2>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            The user id <span className="mono">{id ?? '(missing)'}</span> doesn't match any record.
          </p>
          <div className="mt-4">
            <Link to="/admin/users">
              <Button variant="secondary" size="sm">Back to all users</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return <UserDetailContent user={user} />;
}

function UserDetailContent({ user }: { user: AdminUser }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AdminUser['status']>(user.status);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [resetSentOpen, setResetSentOpen] = useState(false);
  const [forceSignOutOpen, setForceSignOutOpen] = useState(false);
  const [actionBanner, setActionBanner] = useState<{ tone: 'success' | 'info' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function toggleStatus() {
    if (status === 'disabled') {
      setBusy(true);
      try {
        await updateAdminUser(user.id, { status: 'active' });
        setStatus('active');
        setActionBanner({ tone: 'success', text: `${user.name} reactivated. They can sign in again.` });
        void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      } catch (err) {
        setActionBanner({ tone: 'error', text: err instanceof Error ? err.message : 'Could not reactivate the user.' });
      } finally {
        setBusy(false);
      }
    } else {
      setSuspendOpen(true);
    }
  }

  async function confirmSuspend() {
    setBusy(true);
    try {
      await updateAdminUser(user.id, { status: 'suspended' });
      setStatus('disabled');
      setSuspendOpen(false);
      setActionBanner({ tone: 'success', text: `${user.name} suspended. Active sessions revoked; future sign-ins blocked.` });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    } catch (err) {
      setSuspendOpen(false);
      setActionBanner({ tone: 'error', text: err instanceof Error ? err.message : 'Could not suspend the user.' });
    } finally {
      setBusy(false);
    }
  }

  async function sendPasswordReset() {
    setBusy(true);
    try {
      await adminResetUserPassword(user.id);
      setResetSentOpen(true);
    } catch (err) {
      setActionBanner({ tone: 'error', text: err instanceof Error ? err.message : 'Could not send the reset email.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <Link
        to="/admin/users"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 mb-4 transition"
      >
        <ArrowLeft className="w-4 h-4" />
        All users
      </Link>

      {actionBanner && (
        <div className="mb-4">
          <Banner tone={actionBanner.tone} onDismiss={() => setActionBanner(null)}>
            {actionBanner.text}
          </Banner>
        </div>
      )}

      <Card className="p-5 sm:p-6 mb-4">
        <div className="flex flex-col lg:flex-row gap-5 lg:items-center lg:justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <Avatar name={user.name} size="xl" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 truncate">
                  {user.name}
                </h1>
                <Badge tone={STATUS_TONE[status]}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Badge>
                <Badge tone={ROLE_TONE[user.primaryRole]}>{ROLE_LABEL[user.primaryRole]}</Badge>
              </div>
              <div className="text-sm text-zinc-600 dark:text-zinc-300 mt-1 flex items-center flex-wrap gap-x-3 gap-y-1">
                <span className="inline-flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-zinc-400 dark:text-zinc-500" />
                  {user.email}
                </span>
                <span className="text-zinc-400 dark:text-zinc-600">·</span>
                <span>Scope: {user.primaryScope}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {status === 'disabled' ? (
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<CheckCircle2 className="w-4 h-4" />}
                onClick={toggleStatus}
                loading={busy}
              >
                Reactivate
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<ShieldOff className="w-4 h-4" />}
                onClick={toggleStatus}
              >
                Suspend
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<KeyRound className="w-4 h-4" />}
              onClick={sendPasswordReset}
              loading={busy}
            >
              Send password reset
            </Button>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<LogOut className="w-4 h-4" />}
              onClick={() => setForceSignOutOpen(true)}
            >
              Force sign out
            </Button>
          </div>
        </div>
      </Card>

      <SuspendUserModal
        open={suspendOpen}
        onClose={() => !busy && setSuspendOpen(false)}
        user={user}
        onConfirm={confirmSuspend}
        busy={busy}
      />
      <ResetSentModal
        open={resetSentOpen}
        onClose={() => setResetSentOpen(false)}
        email={user.email}
      />
      <ForceSignOutModal
        open={forceSignOutOpen}
        onClose={() => setForceSignOutOpen(false)}
        user={user}
        onSuccess={() => {
          setForceSignOutOpen(false);
          setActionBanner({
            tone: 'success',
            text: `Signed ${user.name} out from every device. Refresh tokens revoked.`,
          });
        }}
      />

      <Card className="px-5 sm:px-6 pt-2 pb-5 sm:pb-6">
        <Tabs
          tabs={[
            { id: 'overview', label: 'Overview', content: <OverviewTab user={user} /> },
            { id: 'memberships', label: 'Memberships', content: <MembershipsTab user={user} />, count: user.memberships?.length ?? 0 },
            { id: 'security', label: 'Security', content: <SecurityTab user={user} /> },
            { id: 'sessions', label: 'Sessions', content: <SessionsTab user={user} /> },
            { id: 'audit', label: 'Audit', content: <AuditTab user={user} /> },
          ]}
        />
      </Card>
    </div>
  );
}

/* ── Overview ───────────────────────────────────────────────────── */

function OverviewTab({ user }: { user: AdminUser }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="p-5 sm:p-6 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Identity" />
        <dl className="space-y-3 text-sm">
          <Row label="Full name" value={user.name} />
          <Row label="Email" value={
            <span className="inline-flex items-center gap-2">
              {user.email}
              {user.emailVerifiedAt
                ? <Badge tone="success">Verified</Badge>
                : <Badge tone="warning">Unverified</Badge>}
            </span>
          } />
          <Row label="Phone" value={
            user.contactPhone ? (
              <span className="inline-flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-zinc-400 dark:text-zinc-500" />
                <span className="mono">{user.contactPhone}</span>
                {user.phoneVerifiedAt
                  ? <Badge tone="success">Verified</Badge>
                  : <Badge tone="neutral">Unverified</Badge>}
              </span>
            ) : <span className="text-zinc-400 dark:text-zinc-500">Not provided</span>
          } />
          <Row label="Language" value={user.language === 'bn' ? 'Bangla (bn)' : 'English (en)'} />
        </dl>
      </Card>

      <Card className="p-5 sm:p-6 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Account" />
        <dl className="space-y-3 text-sm">
          <Row label="User ID" value={<span className="mono">{user.id}</span>} />
          <Row label="Primary role" value={ROLE_LABEL[user.primaryRole]} />
          <Row label="Primary scope" value={user.primaryScope} />
          <Row label="Created" value={formatDate(user.createdAt)} />
          <Row label="Last sign-in" value={user.lastLogin ?? <span className="text-zinc-400 dark:text-zinc-500">Never</span>} />
          <Row label="2FA" value={
            user.mfaEnabled
              ? <Badge tone="success">Enabled</Badge>
              : <Badge tone="warning">Disabled</Badge>
          } />
        </dl>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-zinc-500 dark:text-zinc-400 flex-shrink-0">{label}</dt>
      <dd className="text-zinc-900 dark:text-zinc-100 text-right min-w-0">{value}</dd>
    </div>
  );
}

/* ── Memberships ────────────────────────────────────────────────── */

function MembershipsTab({ user }: { user: AdminUser }) {
  const memberships = user.memberships ?? [];
  if (memberships.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-zinc-500 dark:text-zinc-400">
        No memberships. {user.name} is a {ROLE_LABEL[user.primaryRole].toLowerCase()} with platform-level scope only.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto nice-scroll -mx-6">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
          <tr>
            <th className="text-left font-medium px-6 py-3">Scope</th>
            <th className="text-left font-medium px-4 py-3">Type</th>
            <th className="text-left font-medium px-4 py-3">Role</th>
            <th className="text-left font-medium px-4 py-3">Status</th>
            <th className="text-left font-medium px-6 py-3">Joined</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
          {memberships.map((m) => (
            <tr key={`${m.type}-${m.id}`} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
              <td className="px-6 py-3">
                <div className="font-medium text-zinc-950 dark:text-zinc-50">{m.name}</div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 mono">{m.id}</div>
              </td>
              <td className="px-4 py-3">
                <Badge tone={m.type === 'reseller' ? 'success' : 'warning'}>
                  {m.type === 'reseller' ? 'Reseller' : 'Business'}
                </Badge>
              </td>
              <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{m.role}</td>
              <td className="px-4 py-3">
                <Badge tone="success">Active</Badge>
              </td>
              <td className="px-6 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{formatDate(m.joinedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Security ───────────────────────────────────────────────────── */

function SecurityTab({ user }: { user: AdminUser }) {
  const queryClient = useQueryClient();
  const [forceDisable2faOpen, setForceDisable2faOpen] = useState(false);
  const [twoFaState, setTwoFaState] = useState<'on' | 'off'>(user.mfaEnabled ? 'on' : 'off');
  const [disabling2fa, setDisabling2fa] = useState(false);
  const [forceSignOutOpen, setForceSignOutOpen] = useState(false);
  const [bannerText, setBannerText] = useState<string | null>(null);

  async function confirmDisable2fa() {
    setDisabling2fa(true);
    try {
      await adminDisableUser2fa(user.id);
      setTwoFaState('off');
      setForceDisable2faOpen(false);
      setBannerText(`2FA force-disabled for ${user.name}. All factors and recovery codes were removed.`);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    } catch (err) {
      setForceDisable2faOpen(false);
      setBannerText(err instanceof Error ? err.message : 'Could not disable 2FA.');
    } finally {
      setDisabling2fa(false);
    }
  }

  return (
    <div className="space-y-4">
      {bannerText && (
        <Banner tone="success" onDismiss={() => setBannerText(null)}>
          {bannerText}
        </Banner>
      )}

      {/* 2FA status */}
      <Card className="p-5 sm:p-6 border-zinc-200 dark:border-zinc-700">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className={cn(
              'w-9 h-9 rounded-lg grid place-items-center flex-shrink-0',
              twoFaState === 'on'
                ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400'
                : 'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400',
            )}>
              {twoFaState === 'on' ? <ShieldCheck className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
            </span>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
                Two-factor authentication
              </h3>
              <p className="mt-0.5 text-sm text-zinc-600 dark:text-zinc-300">
                {twoFaState === 'on'
                  ? 'Enabled — sign-ins require a second factor.'
                  : 'Disabled — only password is required to sign in.'}
              </p>
            </div>
          </div>
          <div className="flex-shrink-0">
            {twoFaState === 'on' ? (
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<ShieldOff className="w-4 h-4" />}
                onClick={() => setForceDisable2faOpen(true)}
              >
                Force-disable 2FA
              </Button>
            ) : (
              <Badge tone="neutral">No factor on file</Badge>
            )}
          </div>
        </div>
      </Card>

      {/* Per-user max-sessions override */}
      <SessionCapCard user={user} onSaved={(text) => setBannerText(text)} />

      {/* Force sign-out */}
      <Card className="p-5 sm:p-6 border-zinc-200 dark:border-zinc-700">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <span className="w-9 h-9 rounded-lg grid place-items-center flex-shrink-0 bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400">
              <LogOut className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
                Force sign-out from all devices
              </h3>
              <p className="mt-0.5 text-sm text-zinc-600 dark:text-zinc-300">
                Revokes every active refresh token. Use this if the account is compromised or after a role change.
              </p>
            </div>
          </div>
          <div className="flex-shrink-0">
            <Button
              variant="destructive"
              size="sm"
              iconLeft={<LogOut className="w-4 h-4" />}
              onClick={() => setForceSignOutOpen(true)}
            >
              Sign out everywhere
            </Button>
          </div>
        </div>
      </Card>

      {/* Modals */}
      <Modal
        open={forceDisable2faOpen}
        onClose={() => setForceDisable2faOpen(false)}
        title="Force-disable 2FA"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setForceDisable2faOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" loading={disabling2fa} onClick={() => void confirmDisable2fa()}>
              Force-disable 2FA
            </Button>
          </>
        }
      >
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          Disabling 2FA leaves <strong>{user.name}</strong>'s account protected by password only.
          Use this only when a user has lost their authenticator and verified their identity through other means.
        </p>
        <div className="mt-4">
          <Banner tone="warning">
            <span className="text-sm">
              Action recorded in the audit log under <span className="mono">security.mfa.force_disable</span> ·
              {' '}{user.email} will receive an email notice.
            </span>
          </Banner>
        </div>
      </Modal>

      <ForceSignOutModal
        open={forceSignOutOpen}
        onClose={() => setForceSignOutOpen(false)}
        user={user}
        onSuccess={() => {
          setForceSignOutOpen(false);
          setBannerText(`Signed ${user.name} out from every device. Refresh tokens revoked.`);
        }}
      />
    </div>
  );
}

/* ── Session-cap override card ─────────────────────────────────── */

function SessionCapCard({
  user,
  onSaved,
}: {
  user: AdminUser;
  onSaved: (text: string) => void;
}) {
  const [mode, setMode] = useState<'default' | 'override'>('default');
  const [cap, setCap] = useState<number>(SESSION_CAP_DEFAULT * 2);
  const [saving, setSaving] = useState(false);

  const capValid = cap >= SESSION_CAP_MIN && cap <= SESSION_CAP_MAX;

  async function save() {
    if (mode === 'override' && !capValid) return;
    setSaving(true);
    try {
      await updateAdminUser(user.id, { maxActiveSessionsOverride: mode === 'override' ? cap : null });
      onSaved(
        mode === 'override'
          ? `Active session cap raised to ${cap} for ${user.name}.`
          : `${user.name} now uses the platform default cap (${SESSION_CAP_DEFAULT}).`,
      );
    } catch (err) {
      onSaved(err instanceof Error ? err.message : 'Could not save the session cap.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="p-5 sm:p-6 border-zinc-200 dark:border-zinc-700">
      <CardHeader
        title="Active session cap"
        subtitle={`Platform default: ${SESSION_CAP_DEFAULT} simultaneous devices`}
      />

      <fieldset className="space-y-3">
        <label
          className={cn(
            'flex items-start gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition',
            mode === 'default'
              ? 'border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30'
              : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600',
          )}
        >
          <input
            type="radio"
            name="cap-mode"
            value="default"
            checked={mode === 'default'}
            onChange={() => setMode('default')}
            className="mt-0.5 text-brand focus:ring-brand"
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
              Use platform default
            </div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
              {SESSION_CAP_DEFAULT} simultaneous devices · same as every other account
            </div>
          </div>
        </label>

        <label
          className={cn(
            'flex items-start gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition',
            mode === 'override'
              ? 'border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30'
              : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600',
          )}
        >
          <input
            type="radio"
            name="cap-mode"
            value="override"
            checked={mode === 'override'}
            onChange={() => setMode('override')}
            className="mt-0.5 text-brand focus:ring-brand"
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
              Override for this user
            </div>
            <div className="mt-2 flex items-center gap-2">
              <label htmlFor="session-cap" className="text-sm text-zinc-700 dark:text-zinc-200">
                Cap:
              </label>
              <input
                id="session-cap"
                type="number"
                min={SESSION_CAP_MIN}
                max={SESSION_CAP_MAX}
                step={1}
                value={cap}
                disabled={mode !== 'override'}
                onChange={(e) => setCap(Math.max(SESSION_CAP_MIN, Math.min(SESSION_CAP_MAX, Number(e.target.value) || SESSION_CAP_MIN)))}
                onClick={(e) => {
                  e.stopPropagation();
                  if (mode !== 'override') setMode('override');
                }}
                className={cn(
                  'w-20 px-3 py-1.5 rounded-md text-sm transition tabular-nums',
                  'bg-white text-zinc-950 placeholder-zinc-400',
                  'border border-zinc-300',
                  'focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand',
                  'dark:bg-zinc-900 dark:text-zinc-50 dark:border-zinc-700',
                  'dark:focus:ring-brand-dm dark:focus:border-brand-dm',
                  'disabled:bg-zinc-50 disabled:text-zinc-400 disabled:cursor-not-allowed',
                  'dark:disabled:bg-zinc-900 dark:disabled:text-zinc-500',
                )}
              />
              <span className="text-sm text-zinc-700 dark:text-zinc-200">
                devices <span className="text-zinc-500 dark:text-zinc-400">(range {SESSION_CAP_MIN}–{SESSION_CAP_MAX})</span>
              </span>
            </div>
            {mode === 'override' && !capValid && (
              <div className="mt-2 text-xs text-rose-600 dark:text-rose-400">
                Cap must be between {SESSION_CAP_MIN} and {SESSION_CAP_MAX}.
              </div>
            )}
          </div>
        </label>
      </fieldset>

      <div className="mt-4 flex items-start gap-2 text-xs text-zinc-500 dark:text-zinc-400">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Use sparingly — typical for VIP / shared accounts (e.g. floor-supervisor terminals).
          Raising the cap is recorded as <span className="mono">security.session_cap.update</span> in the audit log.
        </span>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          size="sm"
          loading={saving}
          disabled={mode === 'override' && !capValid}
          onClick={save}
        >
          Save
        </Button>
      </div>
    </Card>
  );
}

/* ── Sessions tab ───────────────────────────────────────────────── */

function SessionIcon({ kind }: { kind: string }) {
  const cls = 'w-4 h-4';
  if (kind === 'laptop') return <Laptop className={cls} />;
  if (kind === 'monitor') return <Monitor className={cls} />;
  if (kind === 'smartphone') return <Smartphone className={cls} />;
  return <Tablet className={cls} />;
}

function SessionsTab({ user }: { user: AdminUser }) {
  // The TARGET user's sessions — GET /admin/users/:id/sessions.
  const { data: sessions = [], refetch } = useUserSessions(user.id);
  const [confirmRevoke, setConfirmRevoke] = useState<AdminUserSessionRow | null>(null);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bannerText, setBannerText] = useState<string | null>(null);

  const activeCount = sessions.length;

  async function revokeSession(id: string) {
    setBusy(true);
    try {
      await revokeUserSessionAdmin(user.id, id);
      await refetch();
      setBannerText('Session revoked. The device will need to sign in again.');
    } catch (err) {
      setBannerText(err instanceof Error ? err.message : 'Could not revoke the session.');
    } finally {
      setBusy(false);
      setConfirmRevoke(null);
    }
  }

  async function signOutEverywhere() {
    setBusy(true);
    try {
      await adminForceSignout(user.id);
      await refetch();
      setBannerText(`Signed ${user.name} out from every device.`);
    } catch (err) {
      setBannerText(err instanceof Error ? err.message : 'Could not sign the user out.');
    } finally {
      setBusy(false);
      setSignOutOpen(false);
    }
  }

  return (
    <div className="space-y-4">
      {bannerText && (
        <Banner tone="success" onDismiss={() => setBannerText(null)}>
          {bannerText}
        </Banner>
      )}

      <Card className="overflow-hidden border-zinc-200 dark:border-zinc-700">
        <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 border-b border-zinc-200 dark:border-zinc-700">
          <div>
            <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
              Active sessions ({activeCount} of {SESSION_CAP_DEFAULT} device cap)
            </h2>
            <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-0.5">
              Devices currently signed in to {user.name}'s account.
            </p>
          </div>
          <Button
            variant="destructive"
            size="sm"
            iconLeft={<LogOut className="w-4 h-4" />}
            onClick={() => setSignOutOpen(true)}
            disabled={activeCount === 0}
          >
            Sign out user
          </Button>
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Device</th>
                <th className="text-left font-medium px-4 py-3">Location</th>
                <th className="text-left font-medium px-4 py-3">Last active</th>
                <th className="text-right font-medium px-5 sm:px-6 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {sessions.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No active sessions — {user.name} isn't signed in anywhere right now.
                  </td>
                </tr>
              ) : (
                sessions.map((s) => (
                  <tr key={s.id} className="transition hover:bg-zinc-50 dark:hover:bg-zinc-800/70">
                    <td className="px-5 sm:px-6 py-3">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-md bg-zinc-100 dark:bg-zinc-800 grid place-items-center text-zinc-500 dark:text-zinc-400">
                          <SessionIcon kind={s.deviceIcon} />
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">
                            {s.deviceLabel}
                          </div>
                          {s.ip && (
                            <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate mono">{s.ip}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200 tabular-nums">
                      {[s.geoCity, s.geoCountry].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">
                      {s.lastActiveAt ? timeAgo(s.lastActiveAt) : '—'}
                    </td>
                    <td className="px-5 sm:px-6 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setConfirmRevoke(s)}>
                        Revoke
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 sm:px-6 py-4 bg-zinc-50/50 dark:bg-zinc-900/50 border-t border-zinc-200 dark:border-zinc-700">
          <p className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            Admins can revoke any session — including the user's current one. Action recorded as
            {' '}<span className="mono">security.session.revoke</span>.
          </p>
        </div>
      </Card>

      {/* Per-session revoke confirm */}
      <Modal
        open={confirmRevoke !== null}
        onClose={() => !busy && setConfirmRevoke(null)}
        title="Revoke session"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmRevoke(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              loading={busy}
              onClick={() => confirmRevoke && revokeSession(confirmRevoke.id)}
            >
              Revoke session
            </Button>
          </>
        }
      >
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          Sign <strong>{user.name}</strong> out of <strong>{confirmRevoke?.deviceLabel}</strong>? The device will
          need to sign in again.
        </p>
      </Modal>

      {/* Sign-out-everywhere confirm */}
      <Modal
        open={signOutOpen}
        onClose={() => !busy && setSignOutOpen(false)}
        title="Sign out from all devices"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setSignOutOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="destructive" loading={busy} onClick={signOutEverywhere}>
              Sign out everywhere
            </Button>
          </>
        }
      >
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          Revoke every active session for <strong>{user.name}</strong>? They will need to sign in
          again on every device. Recorded in the audit log.
        </p>
      </Modal>
    </div>
  );
}

/* ── Audit ──────────────────────────────────────────────────────── */

export function AuditTab({ user }: { user: AdminUser }) {
  const { data: auditLog = [] } = useAuditLog();
  const entries = useMemo(
    () =>
      auditLog.filter(
        (e) => e.actorEmail === user.email || e.subjectId === user.id,
      ),
    [auditLog, user],
  );

  // Group by display date. This hook must run on EVERY render — keep it ABOVE the
  // empty-state early return, or React throws "Rendered more hooks than during the
  // previous render" on the loading→loaded transition (entries: []→[…]).
  const groups = useMemo(() => {
    const map = new Map<string, typeof entries>();
    for (const e of entries) {
      const date = formatDate(e.occurredAt);
      const list = map.get(date) ?? [];
      list.push(e);
      map.set(date, list);
    }
    return Array.from(map.entries());
  }, [entries]);

  if (entries.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-zinc-500 dark:text-zinc-400">
        No audit entries reference {user.name} yet.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groups.map(([date, list]) => (
        <div key={date}>
          <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
            {date}
          </div>
          <ul className="space-y-3">
            {list.map((a) => {
              const isActor = a.actorEmail === user.email;
              return (
                <li key={a.id} className="flex items-start gap-3 text-sm">
                  <span className="w-7 h-7 grid place-items-center rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 flex-shrink-0 mt-0.5">
                    <Calendar className="w-3.5 h-3.5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-900 dark:text-zinc-100">
                      <span className="font-medium mono text-xs">{a.action}</span>
                      <span className="text-zinc-500 dark:text-zinc-400"> · {a.target}</span>
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span>{isActor ? 'Performed by user' : 'Targeted user'}</span>
                      <span>·</span>
                      <span>{a.actor}</span>
                      <span>·</span>
                      <span>{timeAgo(a.occurredAt)}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

/* ── Modals shared by header + security tab ─────────────────────── */

function SuspendUserModal({
  open,
  onClose,
  user,
  onConfirm,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  user: AdminUser;
  onConfirm: () => void;
  busy: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Suspend user"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" loading={busy} onClick={onConfirm}>
            Suspend user
          </Button>
        </>
      }
    >
      <p className="text-sm text-zinc-700 dark:text-zinc-200">
        Suspend <strong>{user.name}</strong>? They will be signed out of every device immediately
        and blocked from signing in again until reactivated.
      </p>
      <div className="mt-4">
        <Banner tone="warning">
          <span className="text-sm">
            Active memberships ({user.memberships?.length ?? 0}) stay intact — reactivation restores access.
            Recorded as <span className="mono">user.suspend</span> in the audit log.
          </span>
        </Banner>
      </div>
    </Modal>
  );
}

function ResetSentModal({
  open,
  onClose,
  email,
}: {
  open: boolean;
  onClose: () => void;
  email: string;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Password reset email sent"
      size="sm"
      footer={
        <Button variant="ghost" onClick={onClose}>Done</Button>
      }
    >
      <p className="text-sm text-zinc-700 dark:text-zinc-200">
        A password reset link has been emailed to <strong className="mono">{email}</strong>.
        The link is valid for 24 hours.
      </p>
    </Modal>
  );
}

function ForceSignOutModal({
  open,
  onClose,
  user,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  user: AdminUser;
  onSuccess: () => void;
}) {
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try {
      await adminForceSignout(user.id);
      onSuccess();
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={open}
      onClose={() => !busy && onClose()}
      title="Sign out from all devices"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" loading={busy} onClick={go}>
            Sign out everywhere
          </Button>
        </>
      }
    >
      <p className="text-sm text-zinc-700 dark:text-zinc-200">
        Revoke every active session for <strong>{user.name}</strong>? They will need to sign in
        again on every device. Recorded in the audit log.
      </p>
    </Modal>
  );
}
